/**
 * @fileoverview Interactive CPA calculator with funnel, break-even, and cited bands.
 * @description Client widget for realized, funnel, and target CPA with cited
 * 2026 bands, a CVR-sensitivity chart, CSV export, and registry-parity FAQs.
 * @module components/resources/calculators/CpaCalculator
 * @exports {Function} CpaCalculator - Interactive CPA calculator widget.
 * @exports {CpaCalculatorProps} CpaCalculatorProps - Optional FAQ override from the route.
 */

'use client'

import { useCallback, useMemo, useState, type KeyboardEvent } from 'react'
import Link from 'next/link'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { FAQ } from '~/components/shared/FAQ'
import { cn } from '~/lib/utils'
import { downloadCsv } from '~/lib/utils/csv'
import { debounce } from '~/lib/utils/events'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { RELATED_GLOSSARY, RELATED_TOOLS } from './CpaCalculator.related'
import {
  CPA_BENCHMARKS,
  DEFAULT_CVR_SWEEP,
  buildCpaExportRows,
  calculateCpa,
  clampNumericInput,
  cpaSensitivityByCvr,
  funnelCpaFromCpc,
  isMedianInsideSpan,
  nearestBenchmark,
  publishedStatisticLabel,
  resolveSensitivityCpc,
  tightestMediaCpaCeiling,
  type CpaMode,
  type CpaVerdict,
  type CpaVerdictBasis,
  type FunnelPath,
} from './CpaCalculator.logic'

const FAQ_ITEMS = [
  {
    question: 'How do you calculate CPA?',
    answer:
      'CPA = total ad spend ÷ number of conversions. If you spent $1,000 and recorded 25 purchases, CPA is $40. Always use the same conversion event in the denominator that the spend was optimized for — mixing leads with purchases silently breaks the number.',
  },
  {
    question: 'What is a good CPA in 2026?',
    answer:
      'A good CPA is one below your break-even (AOV × gross margin) and inside your vertical’s cited band for the same conversion event. WordStream/LocaliQ 2026 puts Google search cost-per-lead at $66.69 and Meta lead CPL at $27.66. Triple Whale’s 2025 ecommerce cohort (restated in 2026 recaps) puts Meta purchase CPA at $38.19 and Google purchase CPA at $23.74. Those are different events — do not average them.',
  },
  {
    question: 'What is the difference between CPA and CAC?',
    answer:
      'CPA is usually a media-only, in-platform metric (ad spend ÷ attributed conversions). CAC is a business-level figure that adds salaries, tools, agency fees, and creative production. Fully loaded CAC is often 2–4× platform CPA. Use CPA to optimize campaigns; use CAC for unit economics. See the CPA vs CAC comparison.',
  },
  {
    question: 'How do I calculate break-even CPA?',
    answer:
      'Break-even CPA = average order value × gross margin. At a $80 AOV and 40% margin, break-even CPA is $32 — anything above that loses contribution profit even when the campaign “works.” This calculator also derives a target CAC (fully loaded ceiling, not media-only CPA) from LTV ÷ desired LTV:CAC ratio, and a target CPA from AOV ÷ target ROAS.',
  },
  {
    question: 'How do CPC, CTR, and CVR turn into CPA?',
    answer:
      'CPA = CPC ÷ conversion rate, or equivalently CPA = CPM ÷ (CTR × CVR × 1,000) when you start from impression cost. A $0.50 CPC at 5% CVR is a $10 CPA. Raising CVR from 2% to 4% halves CPA at the same CPC — that is why landing-page and offer work often beat bid cuts.',
  },
  {
    question: 'Is this CPA calculator free?',
    answer:
      'Yes. It runs in your browser, requires no sign-up, cites every benchmark band to a published 2026 (or latest 2025 restated) source, and exports a CSV of your scenario.',
  },
]

const MODES: ReadonlyArray<{ id: CpaMode; label: string; hint: string }> = [
  { id: 'realized', label: 'Realized CPA', hint: 'Spend ÷ conversions' },
  { id: 'funnel', label: 'Funnel CPA', hint: 'CPC or CPM × rates' },
  { id: 'target', label: 'Target / break-even', hint: 'Margin, LTV, ROAS' },
]
const MODE_IDS: ReadonlyArray<CpaMode> = MODES.map((item) => item.id)
const FUNNEL_PATHS: ReadonlyArray<FunnelPath> = ['cpc', 'cpm']

/**
 * Props for the CPA calculator. The route passes registry FAQs so the visible
 * FAQ and FAQPage JSON-LD cannot drift.
 *
 * @interface CpaCalculatorProps
 * @property {{ question: string; answer: string }[]} [faqItems] - FAQ entries from the registry.
 */
export interface CpaCalculatorProps {
  /** FAQ entries to render. Passed from the registry record so visible copy matches the JSON-LD. */
  faqItems?: { question: string; answer: string }[]
}

/**
 * Formats a USD amount with two decimals, or an em dash when null.
 *
 * @param {number | null} value - Amount to format.
 * @returns {string} Currency string.
 */
function formatUsd(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Formats a multiple (ROAS / LTV:CAC) to one decimal, or an em dash.
 *
 * @param {number | null} value - Multiple to format.
 * @returns {string} Display string.
 */
function formatMultiple(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(1)}×`
}

const VERDICT_TONE: Record<CpaVerdict | 'ceilings_only' | 'need_realized', string> = {
  profitable:
    'border-teal-600 bg-teal-50 text-teal-900 dark:border-teal-400 dark:bg-teal-950/40 dark:text-teal-100',
  unprofitable:
    'border-amber-500 bg-amber-50 text-amber-950 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100',
  on_target:
    'border-slate-400 bg-slate-50 text-slate-800 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100',
  insufficient:
    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
  ceilings_only:
    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
  need_realized:
    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
}

/**
 * Verdict sentence that names the actual comparison ceiling.
 *
 * @param {CpaVerdict | 'ceilings_only' | 'need_realized'} key - Display verdict.
 * @param {CpaVerdictBasis} basis - Ceiling the math compared against.
 * @returns {string} User-facing label.
 */
function verdictLabel(
  key: CpaVerdict | 'ceilings_only' | 'need_realized',
  basis: CpaVerdictBasis,
): string {
  switch (key) {
    case 'profitable':
      return basis === 'break_even'
        ? 'Below break-even — contribution positive'
        : 'Below the ROAS target'
    case 'unprofitable':
      return basis === 'break_even'
        ? 'Above break-even — losing contribution'
        : 'Above the ROAS target'
    case 'on_target':
      return basis === 'break_even'
        ? 'Within 2% of break-even'
        : 'Within 2% of the ROAS target'
    case 'insufficient':
      return 'Add break-even or target inputs to judge the number'
    case 'ceilings_only':
      return 'These are ceilings — switch to Realized or Funnel to judge a campaign'
    case 'need_realized':
      return 'Enter spend and conversions (or funnel rates) to judge this ceiling'
    default: {
      const exhaustive: never = key
      throw new Error(`Unhandled verdict key: ${String(exhaustive)}`)
    }
  }
}

/**
 * Interactive CPA calculator: realized, funnel, and target/break-even modes
 * with cited 2026 bands, a CVR sensitivity chart, and CSV export.
 *
 * @param {CpaCalculatorProps} [props] - Optional FAQ override from the route.
 * @param {{ question: string; answer: string }[]} [props.faqItems] - Registry FAQs for JSON-LD parity.
 * @returns {JSX.Element} The rendered calculator widget.
 */
export function CpaCalculator({
  faqItems = FAQ_ITEMS.map((item) => ({ ...item })),
}: CpaCalculatorProps = {}): JSX.Element {
  const [mode, setMode] = useState<CpaMode>('realized')
  const [funnelPath, setFunnelPath] = useState<FunnelPath>('cpc')
  const [adSpend, setAdSpend] = useState<number | null>(1000)
  const [conversions, setConversions] = useState<number | null>(25)
  const [cpc, setCpc] = useState<number | null>(1.5)
  const [cpm, setCpm] = useState<number | null>(12)
  const [ctrPct, setCtrPct] = useState<number | null>(1.8)
  const [cvrPct, setCvrPct] = useState<number | null>(3)
  const [aov, setAov] = useState<number | null>(80)
  const [grossMarginPct, setGrossMarginPct] = useState<number | null>(40)
  const [ltv, setLtv] = useState<number | null>(240)
  const [targetLtvCacRatio, setTargetLtvCacRatio] = useState<number | null>(3)
  const [targetRoas, setTargetRoas] = useState<number | null>(3)

  const results = useMemo(
    () =>
      calculateCpa({
        mode,
        funnelPath,
        adSpend: adSpend ?? undefined,
        conversions: conversions ?? undefined,
        cpc: cpc ?? undefined,
        cpm: cpm ?? undefined,
        ctrPct: ctrPct ?? undefined,
        cvrPct: cvrPct ?? undefined,
        aov: aov ?? undefined,
        grossMarginPct: grossMarginPct ?? undefined,
        ltv: ltv ?? undefined,
        targetLtvCacRatio: targetLtvCacRatio ?? undefined,
        targetRoas: targetRoas ?? undefined,
      }),
    [
      mode,
      funnelPath,
      adSpend,
      conversions,
      cpc,
      cpm,
      ctrPct,
      cvrPct,
      aov,
      grossMarginPct,
      ltv,
      targetLtvCacRatio,
      targetRoas,
    ],
  )

  const sensitivityCpc = useMemo(
    () => resolveSensitivityCpc(mode, funnelPath, cpc, cpm, ctrPct, results.cpa, cvrPct),
    [mode, funnelPath, cpc, cpm, ctrPct, results.cpa, cvrPct],
  )

  const sensitivity = useMemo(
    () => cpaSensitivityByCvr(sensitivityCpc, DEFAULT_CVR_SWEEP),
    [sensitivityCpc],
  )

  const displayCpa = results.cpa ?? tightestMediaCpaCeiling(
    results.breakEvenCpa,
    results.targetCpaFromRoas,
  )
  const closestBand = useMemo(() => nearestBenchmark(results.cpa), [results.cpa])
  const heroLabel = results.cpa != null ? 'Your CPA' : 'Tightest acquisition ceiling'
  const verdictKey: CpaVerdict | 'ceilings_only' | 'need_realized' =
    results.cpa == null && displayCpa != null
      ? mode === 'target'
        ? 'ceilings_only'
        : 'need_realized'
      : results.verdict

  const activeSensitivityCpa = useMemo(
    () => (sensitivityCpc == null ? null : funnelCpaFromCpc(sensitivityCpc, cvrPct ?? 0)),
    [sensitivityCpc, cvrPct],
  )

  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  const trackInteract = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const interactionType = (args[0] as string) ?? 'input_change'
        sendResourceEvent('interact', {
          resourceType: 'calculator',
          resourceSlug: 'cpa-calculator',
          engagementMs: getDeltaMs(),
          params: { interaction_type: interactionType },
        })
      }, 400),
    [getDeltaMs],
  )

  const exportCsv = useCallback(() => {
    const rows = buildCpaExportRows({
      mode,
      funnelPath,
      adSpend,
      conversions,
      cpc,
      cpm,
      ctrPct,
      cvrPct,
      aov,
      grossMarginPct,
      ltv,
      targetLtvCacRatio,
      targetRoas,
      results,
    })
    downloadCsv('adsights-cpa-calculator.csv', rows)
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'cpa-calculator',
      engagementMs: getTotalMs(),
      params: { outcome: 'export_csv', mode, cpa: results.cpa },
    })
  }, [
    mode,
    funnelPath,
    adSpend,
    conversions,
    cpc,
    cpm,
    ctrPct,
    cvrPct,
    aov,
    grossMarginPct,
    ltv,
    targetLtvCacRatio,
    targetRoas,
    results,
    getTotalMs,
  ])

  return (
    <div className="space-y-10">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <section className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <header className="space-y-1">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Calculate cost per acquisition
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Three ways in: realized CPA from spend and conversions, funnel CPA from CPC or
              CPM × CTR × CVR, and a break-even / target ceiling from margin, LTV:CAC, or ROAS.
              LTV ÷ LTV:CAC is a target CAC (fully loaded), not media-only CPA. Benchmarks stay
              tagged to their conversion event so lead CPL is never averaged into purchase CPA.
            </p>
          </header>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">Mode</legend>
            <div
              className="grid gap-2 sm:grid-cols-3"
              role="radiogroup"
              aria-label="CPA calculation mode"
              onKeyDown={(event) =>
                handleRadioGroupKeyDown(event, MODE_IDS, mode, (next) => {
                  setMode(next)
                  trackInteract('mode_change')
                })
              }
            >
              {MODES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={mode === item.id}
                  tabIndex={mode === item.id ? 0 : -1}
                  onClick={() => {
                    setMode(item.id)
                    trackInteract('mode_change')
                  }}
                  className={cn(
                    'rounded-xl border px-3 py-3 text-left text-sm transition motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600',
                    mode === item.id
                      ? 'border-teal-600 bg-teal-50 text-teal-900 dark:border-teal-400 dark:bg-teal-950/40 dark:text-teal-100'
                      : 'border-slate-200 hover:border-slate-300 dark:border-slate-700',
                  )}
                >
                  <div className="font-medium">{item.label}</div>
                  <div className="mt-1 text-xs opacity-80">{item.hint}</div>
                </button>
              ))}
            </div>
          </fieldset>

          {mode === 'realized' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <NumberField
                id="ad-spend"
                label="Ad spend"
                prefix="$"
                value={adSpend}
                min={0}
                step={50}
                onChange={(value) => {
                  setAdSpend(value)
                  trackInteract('spend_change')
                }}
              />
              <NumberField
                id="conversions"
                label="Conversions"
                value={conversions}
                min={0}
                step={1}
                onChange={(value) => {
                  setConversions(value)
                  trackInteract('conversions_change')
                }}
              />
            </div>
          )}

          {mode === 'funnel' && (
            <div className="space-y-4">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  Start from
                </legend>
                <div
                  className="flex gap-2"
                  role="radiogroup"
                  aria-label="Funnel entry path"
                  onKeyDown={(event) =>
                    handleRadioGroupKeyDown(event, FUNNEL_PATHS, funnelPath, (next) => {
                      setFunnelPath(next)
                      trackInteract('funnel_path_change')
                    })
                  }
                >
                  {FUNNEL_PATHS.map((path) => (
                    <button
                      key={path}
                      type="button"
                      role="radio"
                      aria-checked={funnelPath === path}
                      tabIndex={funnelPath === path ? 0 : -1}
                      onClick={() => {
                        setFunnelPath(path)
                        trackInteract('funnel_path_change')
                      }}
                      className={cn(
                        'rounded-xl border px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600',
                        funnelPath === path
                          ? 'border-teal-600 bg-teal-50 dark:border-teal-400 dark:bg-teal-950/40'
                          : 'border-slate-200 dark:border-slate-700',
                      )}
                    >
                      {path === 'cpc' ? 'CPC + CVR' : 'CPM + CTR + CVR'}
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="grid gap-4 sm:grid-cols-2">
                {funnelPath === 'cpc' ? (
                  <NumberField
                    id="cpc"
                    label="Cost per click"
                    prefix="$"
                    value={cpc}
                    min={0}
                    step={0.1}
                    onChange={(value) => {
                      setCpc(value)
                      trackInteract('cpc_change')
                    }}
                  />
                ) : (
                  <>
                    <NumberField
                      id="cpm"
                      label="CPM"
                      prefix="$"
                      value={cpm}
                      min={0}
                      step={0.5}
                      onChange={(value) => {
                        setCpm(value)
                        trackInteract('cpm_change')
                      }}
                    />
                    <NumberField
                      id="ctr"
                      label="CTR"
                      suffix="%"
                      value={ctrPct}
                      min={0}
                      max={100}
                      step={0.1}
                      onChange={(value) => {
                        setCtrPct(value)
                        trackInteract('ctr_change')
                      }}
                    />
                  </>
                )}
                <NumberField
                  id="cvr"
                  label="Conversion rate"
                  suffix="%"
                  value={cvrPct}
                  min={0}
                  max={100}
                  step={0.1}
                  onChange={(value) => {
                    setCvrPct(value)
                    trackInteract('cvr_change')
                  }}
                />
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField
              id="aov"
              label="Average order value"
              prefix="$"
              value={aov}
              min={0}
              step={5}
              onChange={(value) => {
                setAov(value)
                trackInteract('aov_change')
              }}
            />
              <NumberField
                id="margin"
                label="Gross margin"
                suffix="%"
                value={grossMarginPct}
                min={0}
                max={100}
                step={1}
              onChange={(value) => {
                setGrossMarginPct(value)
                trackInteract('margin_change')
              }}
            />
            <NumberField
              id="ltv"
              label="Customer LTV"
              prefix="$"
              value={ltv}
              min={0}
              step={10}
              onChange={(value) => {
                setLtv(value)
                trackInteract('ltv_change')
              }}
            />
            <NumberField
              id="ltv-ratio"
              label="Target LTV:CAC"
              suffix="×"
              value={targetLtvCacRatio}
              min={0}
              step={0.5}
              onChange={(value) => {
                setTargetLtvCacRatio(value)
                trackInteract('ltv_ratio_change')
              }}
            />
            <NumberField
              id="target-roas"
              label="Target ROAS"
              suffix="×"
              value={targetRoas}
              min={0}
              step={0.25}
              onChange={(value) => {
                setTargetRoas(value)
                trackInteract('roas_change')
              }}
            />
          </div>
        </section>

        <section className="space-y-6">
          <div
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900"
            role="region"
            aria-label="CPA calculation results"
          >
            <div className="flex items-start justify-between gap-3">
              <div aria-live="polite" aria-atomic="true">
                <p className="text-sm font-medium text-slate-500">{heroLabel}</p>
                <p className="mt-1 text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  {formatUsd(displayCpa)}
                </p>
              </div>
              <button
                type="button"
                onClick={exportCsv}
                aria-label="Download this CPA scenario as a CSV file"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                CSV
              </button>
            </div>
            <p
              className={cn(
                'mt-4 rounded-xl border px-3 py-2 text-sm',
                VERDICT_TONE[verdictKey],
              )}
              aria-live="polite"
            >
              {verdictLabel(verdictKey, results.verdictBasis)}
            </p>
            <dl className="mt-5 grid gap-3 sm:grid-cols-2">
              <ResultStat label="Break-even CPA" value={formatUsd(results.breakEvenCpa)} />
              <ResultStat label="Headroom vs break-even" value={formatUsd(results.headroom)} />
              <ResultStat label="Implied ROAS" value={formatMultiple(results.impliedRoas)} />
              <ResultStat label="Implied CPC" value={formatUsd(results.impliedCpc)} />
              <ResultStat label="Target CAC from LTV" value={formatUsd(results.targetCpaFromLtv)} />
              <ResultStat label="Target CPA from ROAS" value={formatUsd(results.targetCpaFromRoas)} />
            </dl>
          </div>

          <BenchmarkTracks cpa={results.cpa} closestId={closestBand?.id ?? null} />
          {mode !== 'target' && sensitivityCpc != null ? (
            <SensitivityChart
              points={sensitivity}
              activeCvr={cvrPct ?? 0}
              activeCpa={activeSensitivityCpa}
            />
          ) : null}
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Cited 2026 CPA / CPL bands
        </h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Each row keeps the publisher’s conversion definition. Lead CPL and purchase CPA are
          different products — the nearest-band highlight is a scale check, not a target.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 dark:border-slate-700">
                <th className="py-2 pr-4 font-medium">Segment</th>
                <th className="py-2 pr-4 font-medium">Low</th>
                <th className="py-2 pr-4 font-medium">Published figure</th>
                <th className="py-2 pr-4 font-medium">Statistic</th>
                <th className="py-2 pr-4 font-medium">High</th>
                <th className="py-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {CPA_BENCHMARKS.map((band) => (
                <tr
                  key={band.id}
                  className={cn(
                    'border-b border-slate-100 dark:border-slate-800',
                    closestBand?.id === band.id && 'bg-teal-50/70 dark:bg-teal-950/30',
                  )}
                >
                  <td className="py-2 pr-4 font-medium text-slate-800 dark:text-slate-100">
                    {band.label}
                    <div className="text-xs font-normal text-slate-500">{band.note}</div>
                  </td>
                  <td className="py-2 pr-4">{formatUsd(band.low)}</td>
                  <td className="py-2 pr-4">{formatUsd(band.median)}</td>
                  <td className="py-2 pr-4">{publishedStatisticLabel(band.statistic)}</td>
                  <td className="py-2 pr-4">{formatUsd(band.high)}</td>
                  <td className="py-2 text-slate-600 dark:text-slate-300">{band.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <RelatedList
          title="Related glossary"
          items={RELATED_GLOSSARY}
          hrefFor={(slug) => `/resources/glossary/${slug}`}
        />
        <RelatedList
          title="Related tools"
          items={RELATED_TOOLS}
          hrefFor={(slug) => `/resources/tools/calculators/${slug}`}
        />
      </section>

      <p className="text-sm text-slate-600 dark:text-slate-300">
        For the segmented write-up behind these bands, see{' '}
        <Link
          href="/blog/topics/ad-performance/what-is-a-good-cpa-2026-benchmarks"
          className="font-medium text-teal-700 underline-offset-2 hover:underline dark:text-teal-300"
        >
          What is a good CPA in 2026?
        </Link>{' '}
        and the{' '}
        <Link
          href="/resources/comparisons/cpa-vs-cac"
          className="font-medium text-teal-700 underline-offset-2 hover:underline dark:text-teal-300"
        >
          CPA vs CAC comparison
        </Link>
        .
      </p>

      {faqItems.length > 0 && (
        <FAQ
          items={faqItems}
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: 'xl' }}
        />
      )}
    </div>
  )
}

/**
 * Implements ARIA radiogroup keyboard behavior (Arrow / Home / End) with roving tabindex.
 *
 * @param {KeyboardEvent<HTMLDivElement>} event - Keydown from the radiogroup container.
 * @param {ReadonlyArray<T>} options - Ordered option values.
 * @param {T} current - Currently selected value.
 * @param {(next: T) => void} onSelect - Selection callback.
 * @template T
 * @returns {void}
 */
function handleRadioGroupKeyDown<T extends string>(
  event: KeyboardEvent<HTMLDivElement>,
  options: ReadonlyArray<T>,
  current: T,
  onSelect: (next: T) => void,
): void {
  if (options.length === 0) return
  const idx = options.indexOf(current)
  const safeIdx = idx >= 0 ? idx : 0
  let nextIdx: number | null = null
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      nextIdx = (safeIdx + 1) % options.length
      break
    case 'ArrowLeft':
    case 'ArrowUp':
      nextIdx = (safeIdx - 1 + options.length) % options.length
      break
    case 'Home':
      nextIdx = 0
      break
    case 'End':
      nextIdx = options.length - 1
      break
    default:
      return
  }
  event.preventDefault()
  const next = options[nextIdx]
  if (next !== undefined) {
    onSelect(next)
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons[nextIdx]?.focus()
  }
}

/**
 * Labeled numeric input used by every CPA field.
 *
 * @param {Object} props - Field props.
 * @param {string} props.id - Input id.
 * @param {string} props.label - Visible label.
 * @param {number | null} props.value - Current value, or null when the field is empty.
 * @param {number} props.min - Minimum.
 * @param {number} [props.max] - Optional maximum.
 * @param {number} props.step - Step increment.
 * @param {string} [props.prefix] - Prefix glyph.
 * @param {string} [props.suffix] - Suffix glyph.
 * @param {(value: number | null) => void} props.onChange - Change handler.
 * @returns {JSX.Element} Labeled number field.
 */
function NumberField({
  id,
  label,
  value,
  min,
  max,
  step,
  prefix,
  suffix,
  onChange,
}: {
  id: string
  label: string
  value: number | null
  min: number
  max?: number
  step: number
  prefix?: string
  suffix?: string
  onChange: (value: number | null) => void
}): JSX.Element {
  return (
    <label className="block space-y-1.5" htmlFor={id}>
      <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</span>
      <span className="flex items-center rounded-xl border border-slate-200 bg-white px-3 focus-within:ring-2 focus-within:ring-teal-600 focus-within:ring-offset-2 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:ring-offset-slate-950">
        {prefix ? <span className="pr-1 text-slate-400">{prefix}</span> : null}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={value ?? ''}
          onChange={(event) => {
            onChange(clampNumericInput(event.target.value, min, max))
          }}
          className="w-full border-0 bg-transparent py-2 text-sm focus:outline-none focus:ring-0"
        />
        {suffix ? <span className="pl-1 text-slate-400">{suffix}</span> : null}
      </span>
    </label>
  )
}

/**
 * Compact result statistic.
 *
 * @param {Object} props - Stat props.
 * @param {string} props.label - Label.
 * @param {string} props.value - Formatted value.
 * @returns {JSX.Element} Definition pair.
 */
function ResultStat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-800/60">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900 dark:text-white">{value}</dd>
    </div>
  )
}

/**
 * Horizontal range tracks for the cited CPA/CPL bands with an optional marker.
 *
 * @param {Object} props - Track props.
 * @param {number | null} props.cpa - Realized CPA to plot.
 * @param {string | null} props.closestId - Highlighted band id.
 * @returns {JSX.Element} Benchmark visualization.
 */
function BenchmarkTracks({
  cpa,
  closestId,
}: {
  cpa: number | null
  closestId: string | null
}): JSX.Element {
  const maxHigh = Math.max(
    ...CPA_BENCHMARKS.flatMap((band) => [band.high, band.median, band.low]),
  )
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
        {cpa != null ? 'Your CPA on cited bands' : 'Cited bands (enter a realized or funnel CPA to plot)'}
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        Teal bar is the named-industry span. The tick is the published average or median —
        WordStream/LocaliQ figures are averages; Triple Whale figures are medians. A published
        figure can sit outside the named-industry span when it is an all-industry cohort value.
        {cpa != null
          ? ' Amber marker is your calculated CPA. Highlighted row is the nearest published figure; compare only inside the same conversion definition.'
          : ' Target ceilings are not plotted here — they are not a realized CPA.'}
      </p>
      <ul className="mt-4 space-y-3">
        {CPA_BENCHMARKS.map((band) => {
          const lowPct = (band.low / maxHigh) * 100
          const highPct = (band.high / maxHigh) * 100
          const medianPct = (band.median / maxHigh) * 100
          const markerPct = cpa != null ? Math.min(100, (cpa / maxHigh) * 100) : null
          const medianInside = isMedianInsideSpan(band)
          return (
            <li key={band.id}>
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span className={cn(closestId === band.id && 'font-semibold text-teal-700 dark:text-teal-300')}>
                  {band.label}
                </span>
                <span>
                  {formatUsd(band.median)} {publishedStatisticLabel(band.statistic)}
                </span>
              </div>
              <div className="relative h-3 rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="absolute inset-y-0 rounded-full bg-teal-200 dark:bg-teal-900"
                  style={{ left: `${lowPct}%`, width: `${Math.max(highPct - lowPct, 1)}%` }}
                />
                <div
                  className={cn(
                    'absolute top-0 h-3 w-0.5',
                    medianInside ? 'bg-teal-700 dark:bg-teal-300' : 'bg-slate-700 dark:bg-slate-200',
                  )}
                  style={{ left: `${medianPct}%` }}
                  role="img"
                  aria-label={
                    medianInside
                      ? `${band.label} ${publishedStatisticLabel(band.statistic)} inside the named-industry span`
                      : `${band.label} all-industry ${publishedStatisticLabel(band.statistic)} sits outside the named-industry span`
                  }
                />
                {markerPct != null ? (
                  <div
                    className="absolute -top-0.5 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-white bg-amber-400 shadow"
                    style={{ left: `${markerPct}%` }}
                    role="img"
                    aria-label={`CPA marker at ${formatUsd(cpa)}`}
                  />
                ) : null}
              </div>
              {!medianInside ? (
                <p className="mt-1 text-[11px] text-slate-500">
                  All-industry {publishedStatisticLabel(band.statistic)} sits outside the
                  named-industry span — not a chart error.
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * SVG line chart of CPA versus conversion rate at the current CPC.
 *
 * @param {Object} props - Chart props.
 * @param {Array<{ cvrPct: number; cpa: number | null }>} props.points - Sensitivity series.
 * @param {number} props.activeCvr - Currently entered CVR.
 * @param {number | null} props.activeCpa - CPA at the entered CVR (interpolated, not nearest sweep point).
 * @returns {JSX.Element} Sensitivity visualization.
 */
function SensitivityChart({
  points,
  activeCvr,
  activeCpa,
}: {
  points: Array<{ cvrPct: number; cpa: number | null }>
  activeCvr: number
  activeCpa: number | null
}): JSX.Element {
  const usable = points.filter((point) => point.cpa != null) as Array<{ cvrPct: number; cpa: number }>
  const width = 360
  const height = 140
  const pad = 28
  const maxCpa = Math.max(
    usable.reduce((max, point) => Math.max(max, point.cpa), 1),
    activeCpa ?? 0,
    1,
  )
  const maxCvr = Math.max(
    usable.reduce((max, point) => Math.max(max, point.cvrPct), 1),
    activeCvr,
    1,
  )
  const x = (cvr: number): number => pad + (cvr / maxCvr) * (width - pad * 2)
  const y = (cpa: number): number => height - pad - (cpa / maxCpa) * (height - pad * 2)
  const d = usable
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(point.cvrPct).toFixed(1)} ${y(point.cpa).toFixed(1)}`)
    .join(' ')

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
        CPA sensitivity to conversion rate
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        Holding CPC constant, doubling CVR halves CPA. The amber dot is your current conversion rate.
      </p>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="mt-3 w-full text-teal-600"
        role="img"
        aria-label="Line chart of CPA falling as conversion rate rises"
      >
        <line x1={pad} y1={height - pad} x2={width - 8} y2={height - pad} stroke="currentColor" strokeOpacity="0.2" />
        <line x1={pad} y1={12} x2={pad} y2={height - pad} stroke="currentColor" strokeOpacity="0.2" />
        {d ? <path d={d} fill="none" stroke="currentColor" strokeWidth="2.5" /> : null}
        {usable.length > 0 && activeCpa != null ? (
          <circle cx={x(activeCvr)} cy={y(activeCpa)} r="5" fill="#f59e0b" />
        ) : null}
        <text x={pad} y={12} className="fill-slate-400" fontSize="9">
          CPA
        </text>
        <text x={width - 40} y={height - 8} className="fill-slate-400" fontSize="9">
          CVR →
        </text>
      </svg>
    </div>
  )
}

/**
 * Related-link list used for glossary and sibling tools.
 *
 * @param {Object} props - List props.
 * @param {string} props.title - Section heading.
 * @param {ReadonlyArray<{ slug: string, label: string, note: string }>} props.items - Entries.
 * @param {(slug: string) => string} props.hrefFor - Href builder.
 * @returns {JSX.Element} Related list.
 */
function RelatedList({
  title,
  items,
  hrefFor,
}: {
  title: string
  items: ReadonlyArray<{ slug: string; label: string; note: string }>
  hrefFor: (slug: string) => string
}): JSX.Element {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      <ul className="mt-3 space-y-3">
        {items.map((item) => (
          <li key={item.slug}>
            <Link
              href={hrefFor(item.slug)}
              className="font-medium text-teal-700 underline-offset-2 hover:underline dark:text-teal-300"
            >
              {item.label}
            </Link>
            <p className="text-sm text-slate-600 dark:text-slate-300">{item.note}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}
